import { FC, useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import ContentTable, {
	type ContentTableColumn,
	type ContentTableItem
} from "src/components/ContentTable/ContentTable";
import Pagination from "src/components/Pagination/Pagination";
import UserChipItem from "src/components/UserSearchItem/UserChipItem";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import useDebounce from "src/hooks/useDebounce/useDebounce";
import usePeople from "src/features/People/hooks/usePeople";
import useAllGroupMemberIds from "../../hooks/useAllGroupMemberIds";
import useAssignGroupMembers from "../../hooks/useAssignGroupMembers";

interface Props {
	isOpen: boolean;
	groupId: number;
	onClose: () => void;
}

const AddGroupMembersModal: FC<Props> = ({ isOpen, groupId, onClose }) => {
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 300);
	const [page, setPage] = useState(1);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	useEffect(() => {
		if (!isOpen) {
			setSearchInput("");
			setPage(1);
			setSelectedIds(new Set());
		}
	}, [isOpen]);

	const { data, isLoading } = usePeople({
		page,
		pageSize: DEFAULT_ADMIN_PAGE_SIZE,
		search: debouncedSearch
	});

	const people = data?.results ?? [];
	const totalCount = data?.count ?? 0;

	const { data: existingMemberIds = [] } = useAllGroupMemberIds(groupId, {
		enabled: isOpen
	});

	const existingSet = useMemo(
		() => new Set(existingMemberIds),
		[existingMemberIds]
	);

	const { mutate: assignMembers, isLoading: isAssigning } =
		useAssignGroupMembers();

	const toggle = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const handleAdd = () => {
		if (selectedIds.size === 0) return;
		assignMembers(
			{ groupId, userIds: Array.from(selectedIds) },
			{ onSuccess: () => onClose() }
		);
	};

	const items: ContentTableItem[] = useMemo(
		() =>
			people.map((person) => ({
				id: person.id,
				name: person.name,
				// `isActive: true` for every row keeps ContentTable from reordering
				// the page (its internal sort puts isActive=true before false).
				// The picker has no draft/live concept; we want the server order.
				isActive: true,
				data: {
					uuid: person.id,
					email: person.email,
					role: person.role,
					storeName: person.store?.name ?? null,
					archived: person.archived
				}
			})),
		[people]
	);

	const userColumns: ContentTableColumn[] = useMemo(
		() => [
			{
				key: "email",
				label: "Email",
				render: (item) => String(item.data?.email ?? "—")
			},
			{
				key: "role",
				label: "Role",
				render: (item) => String(item.data?.role ?? "—")
			},
			{
				key: "store",
				label: "Store",
				render: (item) => String(item.data?.storeName ?? "—")
			},
			{
				key: "select",
				label: "Select",
				width: "160px",
				render: (item) => {
					const id = String(item.id);
					if (existingSet.has(id)) {
						return (
							<span className="text-[13px] text-muted-foreground whitespace-nowrap">
								Already a member
							</span>
						);
					}
					const isSelected = selectedIds.has(id);
					return (
						<>
							<span
								data-testid={isSelected ? "select-checked" : "select-unchecked"}
								className={
									isSelected
										? "inline-flex h-5 w-5 rounded-full bg-primary text-white items-center justify-center"
										: "inline-flex h-5 w-5 rounded-full border border-border"
								}
								aria-hidden="true"
							>
								{isSelected && <Check className="h-3 w-3" />}
							</span>
							<span className="sr-only">
								{isSelected ? "Selected" : "Not selected"}
							</span>
						</>
					);
				}
			}
		],
		[existingSet, selectedIds]
	);

	return (
		<Dialog
			open={isOpen}
			onOpenChange={(open) => {
				if (!open && !isAssigning) onClose();
			}}
		>
			<DialogContent className="max-w-6xl w-[95vw] max-h-[90vh] flex flex-col p-6 gap-4">
				<DialogHeader>
					<DialogTitle className="font-heading text-lg font-medium text-foreground">
						Add members
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto px-1 py-1 -mx-1 -my-1">
					<ContentTable
						items={items}
						isLoading={isLoading}
						columns={userColumns}
						showCreatedBy={false}
						showLastUpdated={false}
						showOrder={false}
						showStatus={false}
						nameColumnLabel="Name"
						searchValue={searchInput}
						onSearchChange={setSearchInput}
						searchPlaceholder="Search people..."
						renderName={(item) => (
							<UserChipItem
								user={{
									uuid: String(item.data?.uuid ?? ""),
									id: 0,
									name: item.name,
									role: String(item.data?.role ?? ""),
									store: 0,
									employee_id: null
								}}
							/>
						)}
						onRowClick={(item) => {
							const id = String(item.id);
							if (existingSet.has(id)) return;
							toggle(id);
						}}
						rowProps={(item) => {
							const id = String(item.id);
							if (existingSet.has(id)) {
								return {
									disabled: true,
									ariaLabel: `${item.name}, already a member`
								};
							}
							const isSelected = selectedIds.has(id);
							return {
								ariaLabel: `Select ${item.name}`,
								ariaPressed: isSelected
							};
						}}
						hideRowMenu
					/>
				</div>

				<Pagination
					page={page}
					pageSize={DEFAULT_ADMIN_PAGE_SIZE}
					totalCount={totalCount}
					onPageChange={setPage}
					itemLabel="people"
				/>

				<div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
					<span className="text-[13px] text-muted-foreground">
						{selectedIds.size} selected
					</span>
					<div className="flex items-center gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={onClose}
							disabled={isAssigning}
						>
							Cancel
						</Button>
						<Button
							type="button"
							onClick={handleAdd}
							disabled={selectedIds.size === 0 || isAssigning}
							aria-label={
								isAssigning
									? "Adding members"
									: selectedIds.size > 0
										? `Add ${selectedIds.size} members`
										: "Add"
							}
							aria-busy={isAssigning}
						>
							{isAssigning ? (
								<>
									<span
										className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
										aria-hidden="true"
									/>
									<span className="sr-only">Adding members…</span>
								</>
							) : (
								`Add ${selectedIds.size > 0 ? `(${selectedIds.size})` : ""}`.trim()
							)}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default AddGroupMembersModal;
